import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PageHeader } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Mail, Phone, UserCircle, Info, Loader2, Clock, Send } from "lucide-react";

const ROLE_COLORS = {
  owner: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  administrator: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  admin: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  supervisor: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  dispatcher: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

export default function SettingsPage() {
  const { user, organization } = useAuth();
  const roleCls = ROLE_COLORS[user?.role] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  const [recipients, setRecipients] = useState("");
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);

  useEffect(() => {
    api.get("/brief-settings")
      .then((r) => { const v = (r.data.recipients || []).join(", "); setRecipients(v); setSaved(v); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const list = recipients.split(",").map((e) => e.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Enter at least one email address."); return; }
    const bad = list.find((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) { toast.error(`"${bad}" is not a valid email.`); return; }
    setSaving(true);
    try {
      const r = await api.put("/brief-settings", { recipients: list });
      const v = (r.data.recipients || []).join(", ");
      setRecipients(v); setSaved(v);
      toast.success("Daily Brief recipients updated.");
    } catch (e) {
      toast.error("Couldn't save recipients.");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post("/brief/send-now");
      toast.success("Test brief is being sent to the recipients above.");
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "Please wait a moment before sending another test." : "Couldn't send test brief.");
    } finally { setSending(false); }
  };

  const sendWeekly = async () => {
    setSendingWeekly(true);
    try {
      await api.post("/weekly/send-now");
      toast.success("Test weekly digest is being sent to the recipients above.");
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "Please wait a moment before sending another test." : "Couldn't send test digest.");
    } finally { setSendingWeekly(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Organization & Account" subtitle="Your organization and signed-in account." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Organization" testid="org-panel">
          <div className="space-y-4 p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600/15 text-blue-400"><Building2 className="h-6 w-6" /></span>
              <div>
                <div className="font-display text-lg font-bold text-white">{organization?.name || "—"}</div>
                <div className="font-mono-data text-xs text-slate-500">{user?.org_id}</div>
              </div>
            </div>
            <div className="space-y-2 border-t border-slate-800 pt-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Data source</span><span className="text-emerald-400">Live RideStamp backend</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Sync</span><span className="text-slate-200">Shared with mobile app</span></div>
            </div>
          </div>
        </Panel>

        <Panel title="Signed-in Account" testid="account-panel">
          <div className="space-y-4 p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700 text-slate-200"><UserCircle className="h-6 w-6" /></span>
              <div>
                <div className="font-display text-lg font-bold text-white">{user?.name}</div>
                <span className={`mt-1 inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono-data text-[11px] font-semibold uppercase tracking-wide ${roleCls}`}>{user?.role}</span>
              </div>
            </div>
            <div className="space-y-2 border-t border-slate-800 pt-4 text-sm">
              <div className="flex items-center gap-2.5 text-slate-300"><Mail className="h-4 w-4 text-blue-400" /> {user?.email}</div>
              {user?.phone && <div className="flex items-center gap-2.5 text-slate-300"><Phone className="h-4 w-4 text-blue-400" /> {user.phone}</div>}
            </div>
          </div>
        </Panel>
      </div>

      <Panel className="mt-6" title="Daily Brief Email" testid="brief-settings-panel">
        <div className="space-y-4 p-5">
          <div className="flex items-start gap-2.5 text-sm text-slate-400">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
            <span>Each morning at <span className="font-semibold text-slate-200">8:30 AM ET</span> we email the Start-of-Day Brief — driver readiness, vehicle health, trip volume and risks — so owners get the readiness summary without logging in.</span>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Recipient email(s)</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} disabled={loading}
                placeholder="owner@company.com, ops@company.com" data-testid="brief-recipients-input"
                className="border-slate-700 bg-slate-900 text-white placeholder:text-slate-500" />
              <Button onClick={save} disabled={saving || loading} data-testid="brief-save-btn"
                className="gap-2 bg-blue-700 text-white hover:bg-blue-800">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
              </Button>
              <Button onClick={sendTest} disabled={sending || loading} variant="outline" data-testid="brief-test-btn"
                className="gap-2 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test now
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">Separate multiple addresses with commas.</p>
            {recipients.trim() !== saved.trim() && (
              <p className="mt-2 text-xs text-blue-300" data-testid="brief-unsaved-hint">Unsaved changes — press Save before sending a test.</p>
            )}
            {/resend\.dev/i.test(saved) && (
              <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="brief-test-recipient-warning">
                This saved list contains a test address (resend.dev) that no person receives. Replace it with real owner emails and press Save.
              </p>
            )}
          </div>
        </div>
      </Panel>

      <Panel className="mt-6" title="Weekly Performance Digest" testid="weekly-settings-panel">
        <div className="space-y-4 p-5">
          <div className="flex items-start gap-2.5 text-sm text-slate-400">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
            <span>Every <span className="font-semibold text-slate-200">Saturday at 5:00 PM ET</span> we email an end-of-week summary — trips completed and on-time rate, top & at-risk drivers, incidents and alerts, plus revenue totals — to the same recipients as the Daily Brief above.</span>
          </div>
          <Button onClick={sendWeekly} disabled={sendingWeekly || loading} variant="outline" data-testid="weekly-test-btn"
            className="gap-2 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
            {sendingWeekly ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test digest now
          </Button>
        </div>
      </Panel>

      <div className="mt-6 flex items-start gap-3 rounded-xl border border-slate-800 bg-[#111827] p-5 text-sm text-slate-400" data-testid="settings-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
        <span>User accounts, roles and permissions are managed in the RideStamp mobile app and shared automatically with this web portal. Any changes made there appear here instantly.</span>
      </div>
    </div>
  );
}
